import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { useAuthState } from "../../Context";
import { Modal } from "../../Components/Modal";
import { ChevronRightIcon, DocumentTextIcon, CheckCircleIcon, PlusCircleIcon } from "@heroicons/react/outline";
import { ModalAlert } from "../../Components/ModalAlert";
import MaintenanceModal from "../../Components/MaintenanceModal";
import Badge from "../../Components/Badge";
import { useRemoteConfig } from "../../Context/firebase/context";
import NPWPServiceCard from "../../Components/NPWPServiceCard";

const menuRegServices = [
  { label: "Registration Website", value: "registration" },
  { label: "Registration Consultation", value: "consultation" },
  { label: "Registration Guide", value: "guide" },
];

function NPWPServiceList(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useAuthState();

  const {
    npwp_registration,
    npwp_validation,
    npwp_validation_url,
    npwp_registration_url,
    npwp_registration_guide_url,
    loading,
  } = useRemoteConfig();

  const [openModal, setopenModal] = useState(false);
  const [modal, setModal] = useState({
    visible: false,
    title: "",
    description: "",
    type: "",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmLinkType, setConfirmLinkType] = useState("");

  const [validationEnabled, setvalidationEnabled] = useState(true);
  const [validationStatus, setvalidationStatus] = useState("");
  const [validationMaintenanceVisible, setvalidationMaintenanceVisible] =
    useState(false);

  const NPWP_SERVICE_ITEMS = [
    {
      id: "npwp-validation",
      titleKey: "NPWP Validation",
      descriptionKey: "Validate NPWP dan Filling Status. Only for registered NPWP.",
      icon: CheckCircleIcon,
      route: "/npwp-validation",
      isVisible: validationStatus !== "DISABLED",
      isEnabled: validationEnabled,
      requiresPremium: true,
      isPremium: true,
      onClick: () => {
        if (!validationEnabled) {
          setvalidationMaintenanceVisible(true);
        } else {
          if (user.membership === "PREMIUM") {
            navigate("/npwp-validation");
          } else {
            setModal({
              visible: true,
              title: t("Premium Feature"),
              description: t(
                "Only premium member can use the validate NPWP feature."
              ),
              type: "WARNING",
            });
          }
        }
      }
    },
    {
      id: "verify-npwp-nik",
      titleKey: "Verify NPWP with NIK & KK",
      descriptionKey: "Validate NPWP dan Filling Status. Only for registered NPWP.",
      icon: DocumentTextIcon,
      route: "#",
      isVisible: true,
      isEnabled: !loading && npwp_validation_url,
      onClick: () => {
        setConfirmLinkType("validate");
        setShowConfirmModal(true);
      }
    },
    {
      id: "npwp-creation",
      titleKey: "NPWP Creation",
      descriptionKey: !user.registeredNpwp?.id && npwp_registration === "enabled" 
        ? "Create a new NPWP. Specifically for users who have not yet registered for NPWP."
        : "Create a new NPWP. Specifically for users who have not yet registered for NPWP.",
      icon: PlusCircleIcon,
      route: "/npwp-preregister",
      isVisible: true,
      isEnabled: !user.registeredNpwp?.id && npwp_registration === "enabled" 
        ? true 
        : (!loading && npwp_registration_url),
      onClick: () => {
        if (!user.registeredNpwp?.id && npwp_registration === "enabled") {
          navigate("/npwp-preregister");
        } else {
          setopenModal(true);
        }
      }
    }
  ];

  const handleClick = (value) => {
    if (value === "registration") {
      setopenModal(false);
      setShowConfirmModal(true);
      setConfirmLinkType("registration");
    } else if (value === "consultation") {
      navigate("/consultation/form");
    } else if (value === "guide") {
      const registrationGuideUrl =
        npwp_registration_guide_url || "https://s.id/eregtutor";
      window.open(registrationGuideUrl, "_blank");
    }
  };

  const onConfirmVisitLink = useCallback(() => {
    setShowConfirmModal(false);
    const linkMap = {
      registration: npwp_registration_url || "https://s.id/eregdaftar",
      validate: npwp_validation_url || "https://s.id/eregcek",
    };
    const link = linkMap[confirmLinkType];
    if (link) window.open(link, "_blank");
  }, [npwp_registration_url, npwp_validation_url, confirmLinkType]);

  const renderRegNpwpModal = () => {
    const filteredMenuRegServices = menuRegServices.filter((item) => {
      if (item.value === "guide" && !npwp_registration_guide_url) {
        return false;
      }
      return true;
    });

    return (
      <Modal
        title="NPWP Creation"
        show={openModal}
        onClose={() => setopenModal(false)}
      >
        <div className="flex flex-col text-sm text-slate-500 p-4 gap-4">
          {filteredMenuRegServices.map((item, index) => (
            <button
              key={index}
              className="flex justify-between items-center text-center gap-4"
              onClick={() => handleClick(item.value)}
            >
              <span>{t(item.label)}</span>
              <ChevronRightIcon className="flex-none h-5 w-5" />
            </button>
          ))}
        </div>
      </Modal>
    );
  };

  const renderMaintenanceBadge = () => {
    const isUnavailable =
      validationStatus === "OFFLINE" ||
      validationStatus === "SCHEDULED_OFFLINE" ||
      validationStatus === "SCHEDULED_ACTIVE";

    const badgeValue = isUnavailable ? "unavailable" : "maintenance";
    const badgeStyle = isUnavailable ? "secondary" : "alert";

    return (
      <div className="flex items-center">
        <Badge title={badgeValue} type={badgeStyle} />
      </div>
    );
  };

  const ServiceListSkeleton = ({ totalItem }) => {
    return (
      <div className="grid grid-cols-6 gap-4">
        {Array.from({ length: totalItem }).map((_, index) => (
          <div key={index} className="col-span-6 sm:col-span-3 lg:col-span-2">
            <div className="bg-white border border-slate-200/60 rounded-xl p-6 h-full flex flex-col animate-pulse">
              <div className="flex items-start space-x-4 flex-1">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                </div>
                <div className="flex-1 min-w-0 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-1"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-4 flex-1"></div>
                  <div className="mt-auto">
                    <div className="w-20 h-10 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <ServiceListSkeleton totalItem={3} />;
  }

  return (
    <div>
      {renderRegNpwpModal()}

      <ModalConfirm
        open={modal.visible}
        setOpen={(open) => {
          setModal((prevModal) => ({
            ...prevModal,
            visible: open,
          }));
        }}
        title={modal.title}
        description={modal.description}
        type={modal.type}
        confirmTitle={t("Subscribe")}
        onConfirm={() => {
          navigate("/subscription");
        }}
      />
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setShowConfirmModal(open);
        }}
        title="thirdPartySites"
        description={
          confirmLinkType === "validate"
            ? "npwpValidateSiteRedirectMessage"
            : "npwpRegisterSiteRedirectMessage"
        }
        confirmButtonText="Continue"
        cancelButtonText="Cancel"
        onConfirm={onConfirmVisitLink}
        type="primary"
      />
      <MaintenanceModal
        isVisible={validationMaintenanceVisible}
        onClose={() => setvalidationMaintenanceVisible(false)}
        onChangeAvailability={(available) => setvalidationEnabled(available)}
        onChangeStatus={(status) => setvalidationStatus(status)}
        configValue={npwp_validation}
      />

      <div className="grid grid-cols-6 gap-4">
        {NPWP_SERVICE_ITEMS.map((item) => (
          <NPWPServiceCard 
            key={item.id} 
            item={item} 
            renderMaintenanceBadge={item.id === "npwp-validation" ? renderMaintenanceBadge : null}
          />
        ))}
      </div>
    </div>
  );
}

export default NPWPServiceList;
